import { useMemo } from 'react';
import { Card, Stack, Title, Text, Badge } from '@repletoryreach/sdk-ui';
import { Alert, WhatsAppPreview } from '@repletoryreach/sdk-ui/data-display';
import type { templates as templatesNs } from '@repletoryreach/sdk-api';
import { formatDate, formatRelative } from '../../../../lib/format';
import { parseComponents } from './parseComponents';
import { useT } from '../../../../i18n/I18nProvider';

type Template = NonNullable<Awaited<ReturnType<typeof templatesNs.get>>['data']>;

const MUTED = 'var(--rr-color-text-muted)';

export function OverviewTab({ template }: { template: Template }) {
  const t = useT();
  const parsed = useMemo(() => parseComponents(template.components), [template.components]);

  const header = parsed.find((c) => c.type === 'HEADER');
  const body = parsed.find((c) => c.type === 'BODY');
  const footer = parsed.find((c) => c.type === 'FOOTER');
  const buttonsBlock = parsed.find((c) => c.type === 'BUTTONS');

  // `parseComponents` swallows a malformed payload and returns an empty array,
  // which used to render as an empty bordered box with no explanation. A
  // non-empty `components` that parses to nothing is a broken record, and the
  // operator should be told so rather than left to guess.
  const unreadable = parsed.length === 0 && Boolean(template.components?.trim());

  return (
    // Preview and metadata side by side where there is room: the design keeps
    // a detail page's panels in columns so the whole record is one screen.
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16, alignItems: 'start' }}>
      <Card withBorder>
        <Stack gap="md">
          <Stack gap="sm">
            <Title order={4}>{t('templates.view.overview.preview')}</Title>
            <Text role="bodySmall" c={MUTED}>
              {t('templates.view.overview.previewHint')}
            </Text>
          </Stack>
          {unreadable
            ? <Alert color="warning">{t('templates.view.overview.parseError')}</Alert>
            : parsed.length === 0
              ? <Text role="bodySmall" c={MUTED}>{t('templates.view.overview.emptyPreview')}</Text>
              : (
                <WhatsAppPreview
                  header={header ? { text: header.text, format: header.format } : null}
                  body={body?.text ?? null}
                  footer={footer?.text ?? null}
                  buttons={buttonsBlock?.buttons ?? null}
                  emptyBodyLabel={t('templates.view.overview.emptyBody')}
                  renderPlaceholder={(position) => (
                    <span
                      style={{
                        background: 'color-mix(in srgb, var(--rr-color-accent) 18%, transparent)',
                        color: 'var(--rr-color-accent)',
                        padding: '0 4px',
                        borderRadius: 4,
                        fontFamily: 'var(--rr-font-mono, ui-monospace, monospace)',
                        fontSize: '0.85em'
                      }}
                    >
                      {`{{${position}}}`}
                    </span>
                  )}
                />
              )}
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="md">
          <Title order={4}>{t('templates.view.overview.metadata')}</Title>
          <Stack gap="sm">
            <Row label={t('common.code')} value={template.code} />
            <Row label={t('templates.columns.category')} value={<Badge size="xs" variant="light">{template.category}</Badge>} />
            <Row label={t('common.language')} value={<Badge size="xs" variant="light">{template.language}</Badge>} />
            <Row label={t('common.created')} value={`${formatDate(template.created_at)} (${formatRelative(template.created_at)})`} />
            <Row label={t('common.updated')} value={`${formatDate(template.updated_at)} (${formatRelative(template.updated_at)})`} />
          </Stack>
        </Stack>
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 8, alignItems: 'baseline' }}>
      <Text role="caption" c={MUTED}>{label}</Text>
      <Text role="bodySmall">{value}</Text>
    </div>
  );
}
